import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { buildUserReportVM } from '../../utils/buildUserReportVM';
import { QC_CONSTANTS } from '../../utils/quickCheckConstants';
import ExposureThermometer from '../../components/quickcheck/ExposureThermometer';
import MaturityRadar from '../../components/quickcheck/MaturityRadar';
import AxisCards from '../../components/quickcheck/AxisCards';
import InterpretationCards from '../../components/quickcheck/InterpretationCards';
import NextSteps from '../../components/quickcheck/NextSteps';
import HowWeCalculate from '../../components/quickcheck/HowWeCalculate';
import SentinelAlert from '../../components/quickcheck/SentinelAlert';
import WeakAxesCallout from '../../components/quickcheck/WeakAxesCallout';
import ConclusaoCanonica from '../../components/quickcheck/ConclusaoCanonica';
import axios from 'axios';

const QuickCheckReport = () => {
  const { surveyId, responseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vm, setVm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReport();
  }, [surveyId, responseId]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/quickcheck/admin/report/${surveyId}/${responseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const reportData = response.data.data;
        
        // Processar dados com o view-model canônico
        const viewModel = buildUserReportVM({
          responses: reportData.responses || reportData.results?.responses,
          participant: reportData.participant,
          completedAt: reportData.completedAt
        });
        
        setVm(viewModel);
      } else {
        setError('Erro ao carregar relatório: ' + response.data.error);
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Erro ao carregar relatório: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate('/admin/quickchecks');
  };

  const downloadPDF = async () => {
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      
      const element = document.getElementById('report-content');
      const opt = {
        margin: [10, 10, 15, 10],
        filename: `QC_NR1_${vm.participante?.name?.replace(/\s+/g, '_') || 'respondente'}_${new Date().toISOString().slice(0,10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] }
      };
      
      html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Por favor, tente novamente.');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <h1>Carregando relatório...</h1>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div style={{ padding: '24px' }}>
          <h1>Erro</h1>
          <p>{error}</p>
          <button onClick={goBack}>Voltar</button>
        </div>
      </DashboardLayout>
    );
  }

  if (!vm) {
    return (
      <DashboardLayout>
        <div style={{ padding: '24px' }}>
          <h1>Erro</h1>
          <p>Não foi possível processar os dados do relatório</p>
          <button onClick={goBack}>Voltar</button>
        </div>
      </DashboardLayout>
    );
  }

  // Verificar se respostas estão incompletas
  if (vm.status === "incompleto") {
    return (
      <DashboardLayout>
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
          <h1>Quick Check NR-1 — Relatório Individual</h1>
          <div style={{ 
            padding: '24px', 
            background: '#fef2f2', 
            border: '1px solid #fecaca',
            borderRadius: '8px',
            marginTop: '24px'
          }}>
            <h2 style={{ color: '#dc2626' }}>Respostas Incompletas</h2>
            <p>As seguintes questões não foram respondidas ou possuem valores inválidos:</p>
            <p style={{ fontFamily: 'monospace', color: '#7f1d1d' }}>
              {vm.missing.join(", ")}
            </p>
            <p>Por favor, solicite ao participante que complete todas as questões antes de gerar o relatório.</p>
          </div>
          <button 
            onClick={goBack}
            style={{
              marginTop: '24px',
              padding: '8px 16px',
              background: '#f1f5f9',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            ← Voltar
          </button>
        </div>
      </DashboardLayout>
    );
  }

  // Renderizar relatório completo
  return (
    <DashboardLayout>
      <div id="report-content" style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', background: '#f8fafc' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '32px',
          padding: '24px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div>
            <h1 style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#1e293b', 
              margin: '0 0 8px 0' 
            }}>
              Quick Check NR-1 — Relatório Individual
            </h1>
            <p style={{ 
              color: '#64748b', 
              margin: '0', 
              fontSize: '1rem' 
            }}>
              {vm.version} • Relatório Canônico Completo
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              onClick={downloadPDF}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: '#6366f1',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              📥 Baixar PDF
            </button>
            <button 
              onClick={goBack}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: '#f1f5f9',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              ← Voltar
            </button>
          </div>
        </div>

        {/* Participant Info */}
        {vm.participante && (
          <div style={{ 
            marginBottom: '24px',
            padding: '20px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              color: '#1e293b',
              marginBottom: '12px'
            }}>
              Informações do Participante
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              <div>
                <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Nome:</span>
                <p style={{ margin: '4px 0', fontWeight: '500', color: '#1e293b' }}>
                  {vm.participante.name || 'Não informado'}
                </p>
              </div>
              <div>
                <span style={{ fontSize: '0.875rem', color: '#64748b' }}>E-mail:</span>
                <p style={{ margin: '4px 0', fontWeight: '500', color: '#1e293b' }}>
                  {vm.participante.email || 'Não informado'}
                </p>
              </div>
              {vm.dataConclusao && (
                <div>
                  <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Data de Conclusão:</span>
                  <p style={{ margin: '4px 0', fontWeight: '500', color: '#1e293b' }}>
                    {vm.dataConclusao}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Results Summary Cards */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          marginBottom: '32px'
        }}>
          <div style={{
            padding: '24px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '500', 
              color: '#64748b',
              marginBottom: '12px'
            }}>
              Índice de Exposição
            </h3>
            <p style={{ 
              fontSize: '3rem', 
              fontWeight: '700', 
              color: '#1e293b',
              margin: '8px 0'
            }}>
              {vm.indices.IE_fmt}
            </p>
            <p style={{
              fontSize: '0.875rem',
              color: '#475569',
              margin: '8px 0 16px 0',
              lineHeight: '1.5',
              fontWeight: '500'
            }}>
              Mede o quanto você está exposto a riscos no trabalho.<br/>
              Escala 0-6: quanto menor, melhor.
            </p>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '0.875rem',
              fontWeight: '600',
              backgroundColor: vm.cores.faixa + '20',
              color: vm.cores.faixa
            }} title={`Classificação baseada na % de respostas positivas (5-6): ${vm.indices.faixa}`}>
              {vm.indices.faixa}
            </span>
            <p style={{
              fontSize: '0.875rem',
              color: '#374151',
              margin: '8px 0 0 0',
              lineHeight: '1.4',
              fontWeight: '500'
            }}>
              {vm.indices.faixa === 'Vermelho' && 'Risco elevado - ação imediata necessária'}
              {vm.indices.faixa === 'Laranja' && 'Risco moderado-alto - atenção necessária'}
              {vm.indices.faixa === 'Amarelo' && 'Risco moderado - monitoramento recomendado'}
              {vm.indices.faixa === 'Verde' && 'Risco baixo - situação favorável'}
            </p>
          </div>

          <div style={{
            padding: '24px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '500', 
              color: '#64748b',
              marginBottom: '12px'
            }}>
              Índice de Maturidade
            </h3>
            <p style={{ 
              fontSize: '3rem', 
              fontWeight: '700', 
              color: '#1e293b',
              margin: '8px 0'
            }}>
              {vm.indices.IM_fmt}
            </p>
            <p style={{
              fontSize: '0.875rem',
              color: '#475569',
              margin: '8px 0 16px 0',
              lineHeight: '1.5',
              fontWeight: '500'
            }}>
              Mede o quão preparada sua organização está para lidar com riscos.<br/>
              Escala 0-6: quanto maior, melhor.
            </p>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '0.875rem',
              fontWeight: '600',
              backgroundColor: '#6366f120',
              color: '#6366f1'
            }} title={`Classificação baseada no Índice de Maturidade: ${vm.indices.nivelIM}`}>
              {vm.indices.nivelIM}
            </span>
            <p style={{
              fontSize: '0.875rem',
              color: '#374151',
              margin: '8px 0 0 0',
              lineHeight: '1.4',
              fontWeight: '500'
            }}>
              {vm.indices.nivelIM === 'Reativo' && 'Organização responde apenas após problemas'}
              {vm.indices.nivelIM === 'Defensivo' && 'Foco em evitar problemas básicos'}
              {vm.indices.nivelIM === 'Em Progresso' && 'Práticas sistemáticas em desenvolvimento'}
              {vm.indices.nivelIM === 'Avançado' && 'Gestão proativa e estruturada'}
              {vm.indices.nivelIM === 'Estratégico' && 'Excelência em gestão de riscos psicossociais'}
            </p>
          </div>
        </div>

        {/* Sentinel Flags Alert */}
        <SentinelAlert vm={vm} />

        {/* Exposure Thermometer */}
        <ExposureThermometer vm={vm} />

        {/* Maturity Radar */}
        <MaturityRadar vm={vm} />

        {/* Weak Axes Callout */}
        <WeakAxesCallout vm={vm} />

        {/* Axis Cards */}
        <AxisCards vm={vm} />

        {/* Interpretation Cards */}
        <InterpretationCards vm={vm} />

        {/* Next Steps */}
        <NextSteps vm={vm} />

        {/* How We Calculate */}
        <HowWeCalculate vm={vm} />

        {/* Conclusao Canonica */}
        <ConclusaoCanonica vm={vm} />

        {/* Footer */}
        <footer style={{
          marginTop: '48px',
          padding: '24px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#64748b',
            marginBottom: '8px'
          }}>
            Este é um autodiagnóstico preliminar baseado na NR-1 – Riscos Psicossociais, 
            obrigatória a partir de 2026.
          </p>
          <p style={{ 
            fontSize: '0.875rem', 
            color: '#64748b',
            marginBottom: '16px'
          }}>
            Para aprofundamento e elaboração de plano de ação personalizado, entre em contato com a Beatt.
          </p>
          <div style={{ 
            fontSize: '0.75rem', 
            color: '#94a3b8',
            borderTop: '1px solid #e2e8f0',
            paddingTop: '16px',
            marginTop: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{vm.version} • Beatt Pulse © {new Date().getFullYear()}</span>
            <span title="Hash SHA-256 das respostas para auditoria">
              Hash: {vm.auditHash ? vm.auditHash.slice(0, 8) : 'n/a'}...
            </span>
          </div>
        </footer>
      </div>
    </DashboardLayout>
  );
};

export default QuickCheckReport;